/**
 * 📤 useEmployeeExport Hook
 * Manages employee export functionality with loading states and error handling
 */

import { useState } from "react";
import { exportEmployees } from "../services/exportService";

export const useEmployeeExport = () => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);

  /**
   * Export employees with loading state management
   * @param {Array} employees - Array of employee objects to export
   * @param {Object} filters - Active filters (searchTerm, filterDepartment, etc.)
   * @param {Object} options - Additional options
   * @param {Function} options.onSuccess - Success callback
   * @param {Function} options.onError - Error callback
   * @returns {Promise<Object>} Export result
   */
  const handleExport = async (employees, filters = {}, options = {}) => {
    const { onSuccess, onError } = options;

    // Validate employees array
    if (!Array.isArray(employees) || employees.length === 0) {
      const error = new Error("No employees to export");
      setExportError(error.message);
      if (onError) onError(error);
      return { success: false, error: error.message };
    }

    try {
      setIsExporting(true);
      setExportError(null);

      // Perform export
      const result = await exportEmployees(employees, filters);

      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (err) {
      console.error("Export failed:", err);
      const errorMessage = err.message || "Failed to export employees";
      setExportError(errorMessage);

      // Call error callback if provided
      if (onError) {
        onError(err);
      }

      return { success: false, error: errorMessage };
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Clear export error
   */
  const clearExportError = () => {
    setExportError(null);
  };

  return {
    isExporting,
    exportError,
    handleExport,
    clearExportError,
  };
};
